import { useIsRestoring, useQuery } from "@tanstack/react-query";
import { useAccessToken, useTenantData } from "@/hooks";
import { TeachersApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, createOpenApiConfiguration } from "@/lib/utils";

/**
 * Teacher profile for public pages (optional auth - same as public class GET).
 * Query key is distinct from admin `useTeacherGet` to avoid cache collisions.
 */
export const useTeacherGetPublic = (
  teacherId: number,
  options?: { enabled?: boolean },
) => {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();
  const isRestoring = useIsRestoring();

  const { data, isError, isLoading, isSuccess, isPending, isFetching, refetch, error } = useQuery({
    queryKey: [QueryKeys.Teacher, teacherId, "public"],
    queryFn: () => {
      if (accessToken) {
        const service = createAuthenticatedApi(TeachersApi, accessToken);
        return service.getTeacherById({ id: teacherId, xTenantID: tenantId });
      }
      const service = new TeachersApi(createOpenApiConfiguration());
      return service.getTeacherById({ id: teacherId, xTenantID: tenantId });
    },
    enabled: options?.enabled !== false && teacherId > 0 && !isRestoring,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    isFetching,
    refetch,
    error,
  };
};
